import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { COL, kwh, n } from "./clienteApi";

const ax = { tick: { fontSize: 10, fill: "#94A3B8" }, axisLine: false, tickLine: false };

const META = {
  produzione: { titolo: "La tua produzione oggi", desc: "Potenza solare in kW · ora per ora" },
  casa: { titolo: "I tuoi consumi oggi", desc: "Consumo in kW · ora per ora" },
  rete: { titolo: "Il tuo scambio con la rete oggi", desc: "Prelievo e immissione in kW" },
};

// Popup Casa/Rete: stesso stile del popup batteria (grafico del giorno + totali recenti)
export function ClienteSlotDialog({ open, kind, onClose, src }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!open) return;
    setD(null);
    src.line("day", format(new Date(), "yyyy-MM-dd")).then(setD).catch(() => setD({ points: [], riepilogo: {} }));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const m = META[kind] || META.casa;
  const r = d?.riepilogo || {};
  const pts = (d?.points || []).map((p) => kind === "produzione"
    ? { t: p.t, produzione: p.pv_w == null ? null : p.pv_w / 1000 }
    : kind === "casa"
    ? { t: p.t, casa: p.load_w == null ? null : p.load_w / 1000 }
    : { t: p.t, prelievo: p.grid_w > 0 ? p.grid_w / 1000 : 0, immissione: p.grid_w < 0 ? -p.grid_w / 1000 : 0 });
  const prov = r.provenienza || {};
  const picco = Math.max(0, ...(d?.points || []).map((p) => p.pv_w || 0));
  const piccoTxt = picco >= 1000 ? `${n(picco / 1000, 2)} kW` : `${n(picco, 0)} W`;
  const tiles = kind === "produzione"
    ? [["#FDE68A", kwh(r.prodotta), "prodotti"], ["#FEF3C7", piccoTxt, "potenza max"],
       ...(prov.fv_diretto != null && r.immessa != null ? [["#DCFCE7", kwh(prov.fv_diretto), "usati subito"], ["#F1F5F9", kwh(r.immessa), "immessi"]] : [])]
    : kind === "casa"
    ? [["#DBEAFE", kwh(r.consumata), "consumati"], ["#FEF3C7", kwh(prov.fv_diretto), "da solare"], ["#DCFCE7", kwh(prov.batteria), "da batteria"], ["#F1F5F9", kwh(prov.rete), "dalla rete"]]
    : [["#F1F5F9", kwh(r.prelevata), "prelevata"], ["#DCFCE7", kwh(r.immessa), "immessa"]];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="cliente-font max-w-md rounded-3xl bg-white text-slate-800" data-testid={`cliente-${kind}-dialog`}>
        <DialogHeader>
          <DialogTitle className="text-lg font-extrabold">{m.titolo}</DialogTitle>
          <DialogDescription className="text-xs font-semibold text-slate-400">{m.desc}</DialogDescription>
        </DialogHeader>
        {!d ? <div className="flex h-56 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div> : (
          <>
            <div className="h-56" data-testid={`cliente-${kind}-grafico`}><ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={pts}>
                <CartesianGrid stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="t" {...ax} interval="preserveStartEnd" minTickGap={40} />
                <YAxis {...ax} width={30} />
                <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} formatter={(v, nm) => [`${n(v, 2)} kW`, nm]} />
                {kind === "produzione" ? (
                  <Area type="monotone" dataKey="produzione" name="Produzione" stroke={COL.sole} fill={COL.sole} fillOpacity={0.25} strokeWidth={1.8} dot={false} connectNulls />
                ) : kind === "casa" ? (
                  <Area type="monotone" dataKey="casa" name="Consumo" stroke={COL.casa} fill={COL.casa} fillOpacity={0.25} strokeWidth={1.6} dot={false} connectNulls />
                ) : (
                  <>
                    <ReferenceLine y={0} stroke="#CBD5E1" />
                    <Area type="monotone" dataKey="prelievo" name="Prelievo" stroke="#64748B" fill="#64748B" fillOpacity={0.25} strokeWidth={1.6} dot={false} />
                    <Area type="monotone" dataKey="immissione" name="Immissione" stroke={COL.batt} fill={COL.batt} fillOpacity={0.25} strokeWidth={1.6} dot={false} />
                  </>
                )}
              </ComposedChart>
            </ResponsiveContainer></div>
            <div className={`grid gap-2 text-center ${tiles.length === 4 ? "grid-cols-4" : "grid-cols-2"}`}>
              {tiles.map(([bg, v, l]) => (
                <div key={l} className="rounded-2xl p-2" style={{ background: bg }} data-testid={`cliente-${kind}-${l.replace(/\s+/g, "-")}`}><b className="block text-sm">{v}</b><span className="text-[11px] text-slate-500">{l}</span></div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
